import * as React from "react";
import gql from "graphql-tag";

import { ApolloClient, withApollo } from "../react-apollo";
import { default as GameState, OTHER_COLOR, Army } from "./GameState";
import GameInterface from "./GameInterface";
import GameDescription from "./GameDescription";
import ArmyLabel from "./ArmyLabel";
import {
  Button,
  Divider,
  Dropdown,
  Grid,
  Header,
  Icon,
  Popup,
  Segment,
} from "../semantic-ui";

const INSPECT_EPD_QUERY = gql`
  query InspectEpd($epd: String!, $uci: String) {
    inspectEpd(epd: $epd, uci: $uci) {
      epd
      legalMoves
      lastEpd
      lastMove
      availableDuels
      winner
    }
  }
`;

type ViewingMode = "pass" | "tablet" | "desktop";

const VIEWING_MODE_NAME = {
  pass: "Pass and Play",
  tablet: "Tablet Mode",
  desktop: "Desktop Mode",
};

const ViewingModeDropdown = ({
  value,
  onChange,
}: {
  value: ViewingMode;
  onChange: (viewingMode: ViewingMode) => void;
}) => (
  <div>
    <Dropdown
      text={VIEWING_MODE_NAME[value]}
      icon="eye"
      floating
      labeled
      button
      className="icon"
    >
      <Dropdown.Menu>
        {(["pass", "tablet", "desktop"] as ViewingMode[]).map(
          (mode: ViewingMode) => (
            <Dropdown.Item
              key={mode}
              active={value === mode}
              onClick={() => onChange(mode)}
            >
              {VIEWING_MODE_NAME[mode]}
            </Dropdown.Item>
          )
        )}
      </Dropdown.Menu>
    </Dropdown>
    <Popup
      trigger={
        <Button icon basic>
          <Icon name="question" />
        </Button>
      }
    >
      <Popup.Header>Viewing Mode</Popup.Header>
      <Popup.Content>
        <Header size="small">Pass and Play</Header>
        <p>
          The active player's pieces are always shown on the bottom of the
          screen.
        </p>
        <Header size="small">Tablet Mode</Header>
        <p>
          Black's controls are shown upside down. Set the display flat on a
          table.
        </p>
        <Header size="small">Desktop Mode</Header>
        <p>White's pieces are always shown on the bottom of the screen.</p>
      </Popup.Content>
    </Popup>
  </div>
);

type Props = {
  client: ApolloClient<any>;
  armies?: [Army, Army];
  epd?: string;
  onGameChange?: (gameState: GameState) => void;
};

type State = {
  isLoading: boolean;
  error: string | null;
  game: GameState;
  // During a duel the challenge bid is stored here
  pendingBid?: number;
  // During a duel the challenge response is stored here
  duelResponse?: string;
  viewingMode: ViewingMode;
};

class LocalGame extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const viewingMode: ViewingMode =
      (localStorage.getItem("LocalGame.viewingMode") as any) || "desktop";
    this.state = {
      isLoading: true,
      error: null,
      game: props.epd
        ? GameState.fromEpd(props.epd)
        : props.armies
        ? GameState.fromArmies(props.armies[0], props.armies[1])
        : GameState.empty,
      viewingMode,
    };
  }

  handleMove = (uci: string | null) => {
    this._updateGame(this.state.game.epd, uci);
  };

  handleStartDuel = (duel: number, bid: number) => {
    const challenge = String(bid);
    if (this.state.duelResponse) {
      this._resolveDuel(challenge, this.state.duelResponse);
    } else {
      this.setState({ pendingBid: bid });
    }
  };

  handleResolveDuel = (bid: number, gain: boolean | undefined) => {
    const response = `${bid}${bid !== 0 ? "" : gain ? "+" : "-"}`;
    if (this.state.pendingBid !== undefined) {
      this._resolveDuel(String(this.state.pendingBid), response);
    } else {
      this.setState({ duelResponse: response });
    }
  };

  handleSkipDuel = () => {
    if (this.state.game.lastEpd && this.state.game.lastMove) {
      this._updateGame(this.state.game.lastEpd, this.state.game.lastMove + ":");
    }
  };

  _resolveDuel(challenge: string, response: string) {
    const { lastEpd, lastMove } = this.state.game;
    if (!lastMove || !lastEpd) return;
    const newMove = `${lastMove}:${challenge}${response}`;
    this._updateGame(lastEpd, newMove);
  }

  _updateGame(epd: string, uci: string | null) {
    this.props.client
      .query({
        query: INSPECT_EPD_QUERY,
        variables: { epd, uci },
      })
      .then(({ data }) => {
        const newGame = new GameState();
        newGame.updateFromServer(data.inspectEpd);
        if (this.props.onGameChange) {
          this.props.onGameChange(newGame);
        }
        this.setState({
          game: newGame,
          isLoading: false,
          error: null,
          pendingBid: undefined,
          duelResponse: undefined,
        });
      })
      .catch((err) => {
        this.setState({ isLoading: false, error: err.message });
      });
  }

  componentDidMount() {
    this._updateGame(this.state.game.epd, null);
  }

  componentDidUpdate(_: Props, oldState: State) {
    if (this.state.game !== oldState.game && this.props.onGameChange) {
      this.props.onGameChange(this.state.game);
    }
  }

  render() {
    const { isLoading, error, game, viewingMode } = this.state;
    if (isLoading) {
      return <div>Loading...</div>;
    } else if (error) {
      return <div>Error: {error}</div>;
    } else {
      // The color who needs to make a decision
      const activeColor =
        this.state.pendingBid !== undefined || game.winner
          ? game.lastMoveColor()
          : game.canChallenge()
          ? OTHER_COLOR[game.lastMoveColor()]
          : game.toMove;
      const flipped = viewingMode === "pass" ? activeColor === "black" : false;
      return (
        <Grid stackable centered>
          <Grid.Column width={8} style={{ maxWidth: 600 }}>
            <GameInterface
              game={game}
              nextAction={
                this.state.pendingBid !== undefined ? "resolve_duel" : "move"
              }
              pendingDuelBid={this.state.pendingBid}
              viewerColor="both"
              flipped={flipped}
              flipBlackActions={viewingMode !== "desktop"}
              onMove={this.handleMove}
              onStartDuel={this.handleStartDuel}
              onSkipDuel={this.handleSkipDuel}
              onResolveDuel={this.handleResolveDuel}
            />
          </Grid.Column>
          <Grid.Column width={8} style={{ maxWidth: 600 }}>
            <Segment>
              <Header as="h4" textAlign="center">
                <Icon name="info" /> Game Details
              </Header>
              <table>
                <tbody>
                  <tr>
                    <th>White:</th>
                    <td>
                      <ArmyLabel color="white" army={game.armies[0]} />
                    </td>
                  </tr>
                  <tr>
                    <th>Black:</th>
                    <td>
                      <ArmyLabel color="black" army={game.armies[1]} />
                    </td>
                  </tr>
                </tbody>
              </table>
              <Header size="small">
                {new GameDescription(
                  game,
                  this.state.pendingBid !== undefined ? "resolve_duel" : "move",
                  undefined
                ).toString()}
              </Header>
              <Divider horizontal>
                <Header as="h4" textAlign="center">
                  <Icon name="eye" /> Viewing Mode
                </Header>
              </Divider>
              <ViewingModeDropdown
                value={viewingMode}
                onChange={this.handleViewingModeChange}
              />
            </Segment>
          </Grid.Column>
        </Grid>
      );
    }
  }

  handleViewingModeChange = (mode: ViewingMode) => {
    this.setState({ viewingMode: mode });
    localStorage.setItem("LocalGame.viewingMode", mode);
  };
}

export default withApollo(LocalGame);
